import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { formatCurrencySuffix } from "common/lib/currency";
import { EXPIRING_SOON_DAYS } from "./ltaOptions";

type Summary = {
  totalLtas: number;
  activeLtas: number;
  expiringSoon: number;
  totalContractors: number;
  totalAgencies: number;
  totalContracts: number;
  totalValue: number;
  activeValue: number;
};

const EMPTY_SUMMARY: Summary = {
  totalLtas: 0,
  activeLtas: 0,
  expiringSoon: 0,
  totalContractors: 0,
  totalAgencies: 0,
  totalContracts: 0,
  totalValue: 0,
  activeValue: 0,
};

const Widget = ({
  label,
  value,
  caption,
  icon,
  color,
  prefix,
  suffix,
  loading,
}: {
  label: string;
  value: number;
  caption?: string;
  icon: string;
  color: string;
  prefix?: string;
  suffix?: string;
  loading: boolean;
}) => (
  <Card className="card-animate">
    <CardBody>
      <div className="d-flex justify-content-between">
        <div className="flex-grow-1 overflow-hidden">
          <p className="fw-medium text-muted mb-0 text-truncate">{label}</p>
          <h2 className="mt-3 mb-1 ff-secondary fw-semibold fs-22">
            {loading ? (
              <Spinner size="sm" color={color} />
            ) : (
              <span className="counter-value">
                <CountUp
                  start={0}
                  prefix={prefix || ""}
                  suffix={suffix || ""}
                  separator=","
                  end={value || 0}
                  decimals={suffix === "M" || suffix === "B" ? 2 : 0}
                  duration={2}
                />
              </span>
            )}
          </h2>
          <p className="mb-0 text-muted fs-12 text-truncate">{caption || ""}</p>
        </div>
        <div>
          <div className="avatar-sm flex-shrink-0">
            <span
              className={`avatar-title rounded-circle fs-4 bg-${color}-subtle text-${color}`}
            >
              <i className={icon}></i>
            </span>
          </div>
        </div>
      </div>
    </CardBody>
  </Card>
);

/**
 * Headline figures above the LTA table.
 *
 * `fetchSummary` is injected so the same widgets serve both the global and the
 * agency-scoped views; the backend applies the scoping.
 */
const LTASummaryWidgets = ({
  fetchSummary,
}: {
  fetchSummary: (params: any) => Promise<any>;
}) => {
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response: any = await fetchSummary({});
        if (!cancelled) setSummary({ ...EMPTY_SUMMARY, ...(response || {}) });
      } catch (e) {
        // The table below still renders; the widgets just stay at zero.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchSummary]);

  const value = formatCurrencySuffix(summary.totalValue || 0);

  return (
    <Row>
      <Col xl={3} md={6}>
        <Widget
          label="Active LTAs"
          value={summary.activeLtas}
          caption={`${summary.totalLtas} agreements in total`}
          icon="ri-file-paper-2-line"
          color="success"
          loading={loading}
        />
      </Col>
      <Col xl={3} md={6}>
        <Widget
          label="Value Under LTAs"
          value={value.value}
          suffix={value.suffix}
          prefix="$"
          caption={`Across ${summary.totalContracts} linked contract${
            summary.totalContracts === 1 ? "" : "s"
          }`}
          icon="ri-money-dollar-circle-line"
          color="primary"
          loading={loading}
        />
      </Col>
      <Col xl={3} md={6}>
        <Widget
          label="Participating UN Agencies"
          value={summary.totalAgencies}
          caption={`${summary.totalContractors} contractor${
            summary.totalContractors === 1 ? "" : "s"
          } engaged`}
          icon="ri-community-line"
          color="info"
          loading={loading}
        />
      </Col>
      <Col xl={3} md={6}>
        <Widget
          label="Expiring Soon"
          value={summary.expiringSoon}
          caption={`Ending within ${EXPIRING_SOON_DAYS} days`}
          icon="ri-timer-flash-line"
          color="warning"
          loading={loading}
        />
      </Col>
    </Row>
  );
};

export default LTASummaryWidgets;
